import { darken, lighten, readableColor, transparentize } from 'polished';

const setuColours = {
    deepPurple: '#20014b',
    flashTurk: '#42cacd',
    crackedYolk: '#feab3e',
    salmonRouge: '#fe90a0',
    fadedMing: '#8db1d1',
    thunderCloud: '#b7cde1',
    pearlyCoke: '#f4f7fa',
    murkyNight: '#091016',

};

const axisColours = {
    axisCrimson: '#ae275d',
    axisCrimsonLight: '#fff5f9'
}

// @ts-ignore
const defaultTheme = {
    spacing: {
        unit: 5,
        sectionHorizontal: ({ spacing }) => spacing.unit * 8,
        sectionVertical: ({ spacing }) => spacing.unit * 8,
    },

    breakpoints: {
        small: '50rem',
        medium: '85rem',
        large: '105rem',
    },

    colors: {
        tonalOffset: 0.3,
        primary: {
            main: setuColours.murkyNight,
            light: ({ colors }) => lighten(colors.tonalOffset, colors.primary.main),
            dark: setuColours.murkyNight,
            contrastText: setuColours.salmonRouge,
        },
        success: {
            main: '#0ec05c',
            light: ({ colors }) => lighten(colors.tonalOffset, colors.success.main),
            dark: ({ colors }) => darken(colors.tonalOffset, colors.success.main),
            contrastText: ({ colors }) => readableColor(colors.success.main),
        },
        warning: {
            main: '#d4ad03',
            light: ({ colors }) => lighten(colors.tonalOffset, colors.warning.main),
            dark: ({ colors }) => darken(colors.tonalOffset, colors.warning.main),
            contrastText: '#ffffff',
        },
        error: {
            main: '#ed4545',
            light: ({ colors }) => lighten(colors.tonalOffset, colors.error.main),
            dark: ({ colors }) => darken(colors.tonalOffset, colors.error.main),
            contrastText: ({ colors }) => readableColor(colors.error.main),
        },
        text: {
            primary: setuColours.deepPurple,
            secondary: ({ colors }) => lighten(colors.tonalOffset, colors.text.primary),
        },
        border: {
            dark: 'rgba(0,0,0, 0.1)',
            light: setuColours.thunderCloud,
        },
        responses: {
            success: {
                color: ({ colors }) => colors.success.main,
                backgroundColor: ({ colors }) => transparentize(0.9, colors.success.main),
            },
            error: {
                color: ({ colors }) => colors.error.main,
                backgroundColor: ({ colors }) => transparentize(0.9, colors.error.main),
            },
            redirect: {
                color: '#ffa500',
                backgroundColor: ({ colors }) => transparentize(0.9, colors.responses.redirect.color),
            },
            info: {
                color: '#87ceeb',
                backgroundColor: ({ colors }) => transparentize(0.9, colors.responses.info.color),
            },
        },
        http: {
            get: setuColours.flashTurk,
            post: '#0ec05c',
            put: '#9b708b',
            options: '#d3ca12',
            patch: '#e09d43',
            delete: '#ed4545',
            basic: '#999',
            link: '#31bbb6',
            head: '#c167e4',
        },
    },
    schema: {
        linesColor: setuColours.thunderCloud,
        defaultDetailsWidth: '75%',
        typeNameColor: theme => theme.colors.text.secondary,
        typeTitleColor: theme => theme.schema.typeNameColor,
        requireLabelColor: theme => theme.colors.error.main,
        labelsTextSize: '0.9em',
        nestingSpacing: '1em',
        nestedBackground: '#fff',
        arrow: {
            size: '1.1em',
            color: setuColours.fadedMing,
        },
    },
    typography: {
        fontSize: '14px',
        lineHeight: '1.5em',
        fontWeightRegular: '400',
        fontWeightBold: '600',
        fontWeightLight: '300',
        fontFamily: 'Matter, sans-serif',
        smoothing: 'antialiased',
        optimizeSpeed: true,
        headings: {
            fontFamily: 'Matter, sans-serif',
            fontWeight: '600',
        },
        code: {
            fontSize: '12px',
            fontFamily: 'GT America Mono, monospace',
            lineHeight: ({ typography }) => typography.lineHeight,
            fontWeight: ({ typography }) => typography.fontWeightRegular,
            color: setuColours.deepPurple,
            backgroundColor: '#e8f8f9',
            wrap: false,
        },
        links: {
            color: ({ colors }) => colors.primary.main,
            visited: ({ typography }) => typography.links.color,
            hover: ({ typography }) => lighten(0.2, typography.links.color),
        },
    },
    menu: {
        width: '260px',
        backgroundColor: '#fff',
        boxShadow: '2px 0 2px -2px rgba(0, 0, 0, 0.24)',
        textColor: setuColours.murkyNight,
        groupItems: {
            textTransform: 'uppercase',
        },
        level1Items: {
            textTransform: 'none',
        },
        arrow: {
            size: '1.5em',
            color: axisColours.axisCrimson,
        },
    },
    logo: {
        maxHeight: ({ menu }) => menu.width,
        maxWidth: ({ menu }) => menu.width,
    },
    rightPanel: {
        backgroundColor: axisColours.axisCrimsonLight,
        width: '44%',
        textColor: setuColours.murkyNight,
    },
    codeSample: {
        backgroundColor: transparentize(0.96, axisColours.axisCrimson),
    },
};

export default defaultTheme;

export function resolveTheme(theme) {
    const resolvedValues: any = {};
    let counter = 0;
    const setProxy = (obj: any, path: string) => {
        Object.keys(obj).forEach(k => {
            const currentPath = (path ? path + '.' : '') + k;
            const val = obj[k];
            if (typeof val === 'function') {
                Object.defineProperty(obj, k, {
                    get() {
                        if (!resolvedValues[currentPath]) {
                            counter++;
                            if (counter > 1000) {
                                throw new Error(
                                    `Theme probably contains cirucal dependency at ${currentPath}: ${val.toString()}`,
                                );
                            }

                            resolvedValues[currentPath] = val(theme);
                        }
                        return resolvedValues[currentPath];
                    },
                    enumerable: true,
                });
            } else if (typeof val === 'object') {
                setProxy(val, currentPath);
            }
        });
    };

    setProxy(theme, '');
    return JSON.parse(JSON.stringify(theme));
}
